<template>
  <div
    id="dossier_layout"
    class="box relative flex h-full flex-col shadow-md sm:mx-5"
  >
    <div
      v-if="
        $slots.cta || withEditionModeButton || withBackButton || title?.length
      "
      class="sticky top-0 right-0 left-0 z-50"
    >
      <DossierInfosHeader
        v-if="!hideDossierInfoHeader"
        :container-scroll-name="'dossier_layout_container'"
        :picture-editable="false"
      />

      <div
        class="flex flex-wrap items-center justify-between gap-3 border-b bg-white/90 p-3 dark:bg-slate-700/90"
      >
        <div
          class="flex min-w-32 flex-1 gap-x-3 sm:min-w-0 sm:flex-none sm:justify-center"
        >
          <div class="flex min-w-0 items-center gap-x-3">
            <FontAwesomeIcon v-if="icon" :icon="['fal', icon]" size="xl" />
            <h2
              class="truncate text-2xl font-bold capitalize-first max-sm:text-xl"
            >
              {{ title }}
            </h2>
          </div>
        </div>
        <div class="ml-auto flex shrink-0 justify-center">
          <div class="flex gap-x-1" :class="ctaClass">
            <slot name="cta" />
            <template v-if="!editionMode">
              <div
                v-if="
                  withEditionModeButton &&
                  !pageWithoutDisplayMode.includes($route.name)
                "
                class="inline-flex divide-x overflow-hidden rounded-md border shadow-sm max-sm:hidden dark:border dark:bg-slate-700 dark:text-white"
                role="group"
              >
                <button
                  type="button"
                  :class="[
                    manualDisplayMode === 'view'
                      ? 'bg-primary-500 text-contrast-primary enabled:hover:bg-primary-500 enabled:hover:text-contrast-primary active:enabled:bg-primary-400'
                      : 'bg-white enabled:hover:bg-primary-100 active:enabled:bg-primary-100 dark:bg-slate-800'
                  ]"
                  class="px-4 text-sm font-medium outline-0 disabled:cursor-not-allowed"
                  @click="switchDisplayMode('view')"
                >
                  <FontAwesomeIcon :icon="['fas', 'grid-2']" />
                </button>
                <button
                  type="button"
                  :class="[
                    manualDisplayMode === 'display'
                      ? 'bg-primary-500 text-contrast-primary enabled:hover:bg-primary-500 enabled:hover:text-contrast-primary active:enabled:bg-primary-400'
                      : 'bg-white enabled:hover:bg-primary-100 active:enabled:bg-primary-100 dark:bg-slate-800'
                  ]"
                  class="px-4 text-sm font-medium outline-0 disabled:cursor-not-allowed"
                  @click="switchDisplayMode('display')"
                >
                  <FontAwesomeIcon :icon="['fas', 'grid-4']" />
                </button>
              </div>
            </template>
            <BaseButton
              v-if="
                $can('manage_dossier') &&
                withEditionModeButton &&
                $route.meta.display_type &&
                $route.meta.switch_type_route
              "
              theme-color="bg-secondary text-contrast-secondary dark:border dark:bg-slate-700 dark:border-secondary dark:hover:bg-secondary-900 dark:text-secondary"
              :icon="$route.meta.display_type === 'edit' ? 'times' : 'pen'"
              :title="
                $route.meta.display_type === 'edit'
                  ? $t('button.cancel')
                  : $t('button.edit')
              "
              :disabled="formSubmitting"
              :display-loader="formSubmitting"
              @click="routeRedirect"
            />
            <BaseButton
              v-if="withBackButton"
              color="dark"
              icon="long-arrow-left"
              :title="$t('button.back')"
              @click="$router.push({ name: backRoute })"
            />
          </div>
        </div>
      </div>
    </div>
    <div>
      <slot name="before_scroll_container" />
    </div>
    <div
      id="dossier_layout_container"
      class="min-h-0 flex-1 bg-gray-50 dark:border-slate-700 dark:bg-slate-700 [&>div]:bg-pattern"
      :class="[
        notScrollable ? 'overflow-hidden' : 'overflow-y-auto rounded-b-md'
      ]"
    >
      <slot class="" />
    </div>
    <div
      v-if="!hideFooterBar"
      class="sticky right-0 bottom-0 left-0 z-50 flex flex-row items-center gap-x-3 rounded-b-md border-t bg-white/90 dark:bg-slate-700/90"
    >
      <div class="flex-1 px-3 max-sm:empty:hidden">
        <slot name="footer" />
      </div>
      <div
        class="flex shrink-0 flex-row gap-x-3 p-3 empty:hidden max-sm:flex-1 max-sm:flex-wrap max-sm:justify-end max-sm:gap-y-2 max-sm:pb-6"
      >
        <BaseButton
          v-if="prevButtonCondition"
          theme-color="bg-secondary text-contrast-secondary dark:border dark:bg-slate-600/90 dark:border-secondary dark:hover:bg-secondary-900 dark:text-secondary"
          :title="$t('button.previous')"
          icon="chevron-left"
          :disabled="formSubmitting"
          :display-loader="formSubmitting"
          inverse-icon-order
          @click="
            handleMoveButtonWithoutSave(firstMenuPreviousEntry?.routeName)
          "
        />
        <BaseButton
          v-if="prevAndSaveButtonCondition"
          theme-color="bg-secondary text-contrast-secondary dark:border dark:bg-slate-600/90 dark:border-secondary dark:hover:bg-secondary-900 dark:text-secondary"
          icon="chevron-left"
          :title="$t('button.previous_and_save')"
          :disabled="formSubmitting"
          :display-loader="formSubmitting"
          inverse-icon-order
          @click="handleMoveAndSaveButton(firstMenuPreviousEntry?.routeName)"
        />
        <BaseButton
          v-if="nextAndSaveButtonCondition || nextAndSaveButton"
          theme-color="bg-secondary text-contrast-secondary dark:border dark:bg-slate-600/90 dark:border-secondary dark:hover:bg-secondary-900 dark:text-secondary"
          icon="chevron-right"
          :title="$t('button.next_and_save')"
          :disabled="formSubmitting"
          :display-loader="formSubmitting"
          @click="handleMoveAndSaveButton(firstMenuNextEntry?.routeName)"
        />
        <BaseButton
          v-if="nextButtonCondition"
          theme-color="bg-secondary text-contrast-secondary dark:border dark:bg-slate-600/90 dark:border-secondary dark:hover:bg-secondary-900 dark:text-secondary"
          icon="chevron-right"
          :title="$t('button.next')"
          :disabled="formSubmitting"
          :display-loader="formSubmitting"
          @click="handleMoveButtonWithoutSave(firstMenuNextEntry?.routeName)"
        />
        <BaseButton
          v-if="submitButtonCondition || displaySubmitButtonWithoutEditionMode"
          data-testid="dossier-save-button"
          icon="paper-plane"
          theme-color="bg-primary text-contrast-primary dark:border dark:bg-slate-600/90 dark:border-primary dark:hover:bg-primary-900 dark:text-primary"
          :title="$t('button.save')"
          :disabled="formSubmitting || !dossierDataChanged"
          :display-loader="formSubmitting"
          @click="handleSubmitMethod($route.meta.switch_type_route)"
        />
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapMutations } from 'vuex'
import { toRaw } from 'vue'
import BaseUnsavedChangesNotificationMixin from '@u/mixins/BaseUnsavedChangesNotificationMixin'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import { PlaceType } from '@/assets/enums'
import MenuDossierMixin from '@u/mixins/Dossier/MenuDossierMixin'
import DossierInfosHeader from '@/views/dossier/form/components/DossierInfosHeader.vue'
import WalkmanButton from '@c/WalkmanButton.vue'

export default {
  name: 'BaseDossierPageLayout',
  components: {
    WalkmanButton,
    DossierInfosHeader,
    BaseButton,
    FontAwesomeIcon
  },
  mixins: [BaseUnsavedChangesNotificationMixin, MenuDossierMixin],
  props: {
    title: {
      type: String,
      required: false,
      default: null
    },
    icon: {
      type: String,
      required: false,
      default: null
    },
    ctaClass: {
      type: String,
      required: false,
      default: ''
    },
    withBackButton: {
      type: Boolean,
      required: false,
      default: false
    },
    withSubmitButton: {
      type: Boolean,
      required: false,
      default: true
    },
    withEditionModeButton: {
      type: Boolean,
      required: false,
      default: true
    },
    withNextButton: {
      type: Boolean,
      required: false,
      default: false
    },
    nextAndSaveButton: {
      type: Boolean,
      required: false,
      default: false
    },
    submitMethod: {
      type: Function,
      required: false,
      default: null
    },
    backRoute: {
      type: String,
      required: false,
      default: null
    },
    displaySubmitButtonWithoutEditionMode: {
      type: Boolean,
      required: false,
      default: false
    },
    hideFooterBar: {
      type: Boolean,
      required: false,
      default: false
    },
    notScrollable: {
      type: Boolean,
      required: false,
      default: false
    },
    dossierDataChanged: {
      type: Boolean,
      required: false,
      default: false
    },
    hideDossierInfoHeader: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['canceled'],
  data() {
    const storedPreference = localStorage.getItem(
      `view_pref_${this.$store.getters['auth/getUser']?.id}`
    )
    return {
      formSubmitting: false,
      manualDisplayMode: storedPreference ?? this.userDisplayMode
    }
  },
  computed: {
    PlaceType() {
      return PlaceType
    },
    ...mapGetters({
      editionMode: 'dossier/getEditionMode',
      userDisplayMode: 'auth/getDisplayMode',
      errors: 'auth/getErrors',
      dossierHasChanged: 'dossier/getHasChanged'
    }),
    pageWithoutDisplayMode() {
      return [
        'dossier_form_financing_route',
        'dossier_form_financing_route_display',
        'dossier_form_particularities_route_display',
        'dossier_form_particularities_route'
      ]
    },
    prevAndSaveButtonCondition() {
      if (!this.withSubmitButton || !this.submitMethod) return false
      return this.firstMenuHasPreviousEntry && this.dossierDataChanged
    },
    nextAndSaveButtonCondition() {
      if (!this.withSubmitButton || !this.submitMethod) return false
      return this.firstMenuHasNextEntry && this.dossierDataChanged
    },
    prevButtonCondition() {
      return (
        this.firstMenuHasPreviousEntry &&
        this.withNextButton &&
        !this.dossierDataChanged
      )
    },
    nextButtonCondition() {
      return (
        this.firstMenuHasNextEntry &&
        this.withNextButton &&
        !this.dossierDataChanged
      )
    },
    submitButtonCondition() {
      if (!this.withSubmitButton || typeof this.submitMethod !== 'function')
        return false
      return this.editionMode
    },
    errorsIsEmpty() {
      return Object.keys(toRaw(this.errors)).length === 0
    }
  },
  watch: {
    '$route.meta.display_type'(newValue, oldValue) {
      if (['view', 'display'].includes(this.$route.meta.display_type)) {
        this.processSetEditionModeMutation(false)
      } else if (this.$route.meta.display_type === 'edit') {
        this.processSetEditionModeMutation(
          this.$can('manage_dossier') ? true : false
        )
      }
    }
  },
  beforeMount() {
    this.processSetLeaveRouteByButtonMutation(false)
    this.processSetLeaveRouteByNotificationMutation(false)
    if (['view', 'display'].includes(this.$route.meta.display_type)) {
      this.processSetEditionModeMutation(false)
    } else if (this.$route.meta.display_type === 'edit') {
      this.processSetEditionModeMutation(true)
    }
  },
  methods: {
    ...mapMutations({
      processSetEditionModeMutation: 'dossier/setEditionMode',
      processSetLeaveRouteByButtonMutation: 'dossier/setLeaveRouteByButton',
      processSetLeaveRouteByNotificationMutation:
        'dossier/setLeaveRouteByNotification'
    }),
    switchDisplayMode(mode) {
      const { switch_type_route } = this.$route.meta
      let nextRoute = null

      if (typeof switch_type_route === 'string') {
        if (mode === 'edit') {
          nextRoute = switch_type_route
        } else {
          nextRoute = this.$route.name
          nextRoute = switch_type_route.replace(/_edit$/, '')
          if (mode === 'display') {
            nextRoute += '_display'
          }
        }
      } else if (typeof switch_type_route === 'object') {
        nextRoute = switch_type_route[mode]
      }

      if (['display', 'view'].includes(mode)) {
        this.$store.commit('auth/setDisplayMode', mode)
      }

      if (!nextRoute) nextRoute = this.$route.name

      if (nextRoute && this.$route.name !== nextRoute) {
        this.$router.push({
          name: nextRoute,
          params: { ...this.$route.params },
          meta: { ...this.$route.meta, display_type: mode }
        })
      }

      this.manualDisplayMode = mode

      this.processSetEditionModeMutation(
        this.$route.meta.display_type === 'edit' && this.$can('manage_dossier')
      )
    },
    routeRedirect() {
      let nextMode = null
      if (this.manualDisplayMode === 'edit') {
        nextMode = this.$store.getters['auth/getDisplayMode']
      } else {
        nextMode = 'edit'
      }
      this.switchDisplayMode(nextMode)
    },
    handleMoveAndSaveButton(route_name) {
      this.formSubmitting = true

      if (typeof route_name === 'object') {
        route_name = route_name[this.$store.getters['auth/getDisplayMode']]
      }

      this.submitMethod()
        .then((_response) => {
          this.formSubmitting = false
          if (this.errorsIsEmpty) {
            //this.processSetLeaveRouteByButtonMutation(true)
            this.$router.push({
              name: route_name,
              params: { id: this.$route.params.id },
              meta: { isDossierPage: true }
            })
          }
        })
        .catch((_reason) => {
          this.formSubmitting = false
        })
    },
    handleMoveButton(route_name) {
      this.processSetLeaveRouteByButtonMutation(true)
      if (this.dossierHasChanged) {
        this.processSetLeaveRouteByNotificationMutation(true)
        this.handleUnsavedChangesNotification(
          {
            name: route_name,
            params: { id: this.$route.params.id }
          },
          this.submitMethod
        )
      } else {
        this.$router.push({
          name: route_name,
          params: { id: this.$route.params.id }
        })
      }
    },
    handleMoveButtonWithoutSave(route_name) {
      this.processSetLeaveRouteByNotificationMutation(false)

      if (typeof route_name === 'object') {
        route_name = route_name[this.$store.getters['auth/getDisplayMode']]
      }

      this.$router.push({
        name: route_name,
        params: { id: this.$route.params.id }
      })
    },
    handleSubmitMethod(route_name) {
      if (typeof route_name === 'object') {
        route_name = route_name[this.$store.getters['auth/getDisplayMode']]
      }

      if (typeof this.submitMethod === 'function') {
        this.formSubmitting = true
        this.submitMethod()
          .then((_response) => {
            this.formSubmitting = false
            if (this.errorsIsEmpty) {
              this.processSetLeaveRouteByButtonMutation(true)
              this.$router.push({
                name: route_name,
                params: { id: this.$route.params.id },
                meta: { isDossierPage: true }
              })
            }
          })
          .catch((_reason) => {
            this.formSubmitting = false
          })
      }
    },
    // routeRedirect() {
    //   this.$router.push({ name:
    //       this.$route.meta.display_type === 'edit' &&
    //       this.$route.meta.switch_type_route[this.displayMode] ?
    //         this.$route.meta.switch_type_route[this.displayMode] :
    //          this.$route.meta.switch_type_route
    //   })
    // },
    switchEditionMode(new_state) {
      if (this.editionMode) {
        this.$emit('canceled')
      }
      this.processSetEditionModeMutation(new_state)
    }
  }
}
</script>
